import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { useAuth } from '@/context/AuthContext';
import { useUnriddle } from '@/context/UnriddleContext';
import useUpdateUser from '@/context/useUpdateUser';
import useScreenSize from '@/hooks/use-screen-size';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { cn } from '@/utils/utils';
import { formatTimeAgo } from '@unriddle/api/slack';
import { useRouter } from 'next/router';
import { parseAsString, useQueryState } from 'nuqs';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import OnboardingFooter from '../../Onboarding/OnboardingFooter';
import OnboardingHeader from '../../Onboarding/OnboardingHeader';
import OnboardingPurpose from './purpose';
import type { OnboardingAnswers } from './questions';
import OnboardingQuestions from './questions';
import OnboardingReferral from './referral';

export type OnboardingStep = 'purpose' | 'questions' | 'referral' | 'legal';

const OnboardingModal = () => {
  const { user } = useAuth();
  const { userCountry } = useUnriddle();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('purpose');
  const [answers, setAnswers] = useState<OnboardingAnswers>({});
  const [selectedPurpose, setSelectedPurpose] = useState<
    'work' | 'study' | 'personal' | null
  >(null);
  const [isStepComplete, setIsStepComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { track } = useAnalytics();
  const { updateUser } = useUpdateUser();
  const router = useRouter();
  const { mutateAsync: sendMessageToSlack } = api.slack.message.useMutation();
  const { mutate: upsertAffiliate, isPending: isUpsertingAffiliate } =
    api.referral.upsertAffiliate.useMutation();
  const [affiliateUrl, setAffiliateUrl] = useState<string | null>(null);
  const { mutateAsync: upsertOnboardingResponse } =
    api.userOnboarding.upsert.useMutation();
  const posthog = usePostHog();
  const { height } = useScreenSize();

  const [chatbotId] = useQueryState('chatbotId', parseAsString);

  useEffect(() => {
    const query = { ...router.query, onboarding: 'true' };
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });

    return () => {
      const { onboarding, ...restQuery } = router.query;
      router.replace(
        { pathname: router.pathname, query: restQuery },
        undefined,
        { shallow: true },
      );
    };
  }, []);

  useEffect(() => {
    if (
      user &&
      !user.isOnboarded &&
      typeof window !== 'undefined' &&
      window.gtag
    ) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-16774945281/OXIACKLMueoZEIG09b4-',
      });
    }
  }, [user]);

  const checkStepCompletion = (updatedAnswers: { [x: string]: unknown }) => {
    console.log('updatedAnswers', updatedAnswers);
    if (currentStep === 'questions') {
      const requiredQuestions =
        answers.purpose === 'work'
          ? ['workType', 'workRole', 'companySize', 'hearAboutUs']
          : answers.purpose === 'study'
            ? ['educationLevel', 'hearAboutUs']
            : answers.purpose === 'personal'
              ? ['hearAboutUs']
              : [];
      const isComplete = requiredQuestions.every(
        (question) => updatedAnswers[question],
      );
      setIsStepComplete(isComplete);
    }
  };

  const formatSlackMessage = (answers: {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    workType?: any;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    workRole?: any;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    companySize?: any;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    hearAboutUs?: any;
  }) => {
    const createdAt = new Date(user ? user.createdAt : '');

    return `New Work user:
  - Email: ${user ? user.email : 'N/A'}
  - Work type: ${answers.workType}
  - Work role: ${answers.workRole}
  - Company size: ${answers.companySize}
  - Heard about us from: ${answers.hearAboutUs}
  - Country: ${userCountry}
  - Account created: ${formatTimeAgo(createdAt)}`;
  };

  const handleNextStep = async (answer: string) => {
    const updatedAnswers = { ...answers, [currentStep]: answer };
    setAnswers(updatedAnswers);

    switch (currentStep) {
      case 'purpose':
        setCurrentStep('questions');
        break;
      case 'questions':
        setCurrentStep('referral');
        upsertAffiliate(
          {},
          {
            onSuccess: (data) => {
              setAffiliateUrl(data?.url || '');
            },
          },
        );

        break;
      case 'referral':
        setIsLoading(true);

        if (updatedAnswers.purpose === 'work') {
          const formattedMessage = formatSlackMessage(updatedAnswers);
          try {
            await sendMessageToSlack({
              message: formattedMessage,
              type: 'workSignup',
            });
          } catch (error) {
            console.error('Failed to send message to Slack', error);
          }
        }
        if (user) {
          localStorage.setItem(`isOnboarded_${user.id}`, 'true');
        }
        track('Onboarding Complete', updatedAnswers);

        if (chatbotId) router.push(`/b/${chatbotId}`);

        try {
          if (user) {
            await updateUser({
              email: user.email,
              isOnboarded: true,
              ...updatedAnswers,
            });
            await upsertOnboardingResponse(updatedAnswers);
            posthog.setPersonProperties(updatedAnswers);
          } else {
            console.error('User is null or undefined');
          }
        } catch (error) {
          console.error('Failed to update onboarding response', error);
        } finally {
          setIsLoading(false);
        }
        break;
    }
    setIsStepComplete(false);
  };

  const handlePurposeSelect = (option: 'work' | 'study' | 'personal') => {
    setSelectedPurpose(option);
    setIsStepComplete(true);
  };

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const handleSelect = (option: { value: any }, question: string) => {
    setAnswers((prevAnswers) => {
      const updatedAnswers = { ...prevAnswers, [question]: option.value };
      console.log('Updated answers in handleSelect:', updatedAnswers);
      checkStepCompletion(updatedAnswers);
      return updatedAnswers;
    });
  };

  const handleBack = () => {
    const steps: OnboardingStep[] = ['purpose', 'questions', 'referral'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  if (!user || user?.isOnboarded) {
    return null;
  }

  return (
    <Dialog open={user.isOnboarded === false}>
      <DialogContent
        className="!w-screen h-dvh max-w-none m-0 p-0 rounded-[0px]"
        hideCloseButton={true}
      >
        <div className="w-full h-full flex flex-col justify-center items-center">
          <OnboardingHeader
            onBack={handleBack}
            disabled={currentStep === 'purpose'}
          />
          <div
            className={cn(
              'w-full max-w-sm flex flex-col items-stretch px-4',
              height < 700 && 'pt-24',
            )}
          >
            {currentStep === 'purpose' && (
              <OnboardingPurpose
                handlePurposeSelect={handlePurposeSelect}
                selectedPurpose={selectedPurpose}
                handleNextStep={() => {
                  if (selectedPurpose) handleNextStep(selectedPurpose);
                }}
              />
            )}

            {currentStep === 'questions' && (
              <OnboardingQuestions
                handleSelect={handleSelect}
                handleNextStep={() =>
                  handleNextStep(
                    answers[currentStep as keyof OnboardingAnswers] || '',
                  )
                }
                disabled={!isStepComplete || isLoading}
                purpose={selectedPurpose}
              />
            )}
            {currentStep === 'referral' && (
              <OnboardingReferral
                handleNextStep={() => handleNextStep('referral')}
                affiliateUrl={affiliateUrl}
                isUpsertingAffiliate={isUpsertingAffiliate}
              />
            )}
          </div>
          <OnboardingFooter variant={currentStep} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
